/**
 * ehr#689 (Jay 10-30, ruling #4): where opening a session attendee's note goes.
 *
 * A family / couples attendee's note opens on its session page (Couples and Family Notes) with
 * that attendee selected, wherever it is opened from: the chart's Clinical Notes list, the
 * Progress Notes To review queue, a Dashboard or Alerts Hub card, or a deep link
 * (/?contact=<id>&view=clinical_notes&note=<noteId>). The page shows the attendees the caller can
 * reach (ehr#687 session access: a reviewer sees the members whose notes they review), Pending
 * review and Co-sign all, so a supervisor reviews the note with its session around it.
 *
 * Group notes keep their own routing for now: the same question for group (#688 Q2) is not
 * answered. To route them the same way later, add 'group' to SESSION_PAGE_ON_OPEN and handle
 * `kind === 'group'` where `sessionPageFor` is called (the group workspace, ?group=&focus=).
 */
export type SessionPageKind = 'family' | 'group';

/** The session kinds whose attendee notes open on the session page from any entry point. */
export const SESSION_PAGE_ON_OPEN: ReadonlySet<SessionPageKind> = new Set<SessionPageKind>([
  'family',
]);

export interface SessionNoteRef {
  groupSessionId?: string | null;
  isGroup?: boolean | null;
}

/** The session a note belongs to: a group session (isGroup) or a family / couples one. */
export function sessionOf(n: SessionNoteRef): { kind: SessionPageKind; sessionId: string } | null {
  if (!n.groupSessionId) return null;
  return { kind: n.isGroup ? 'group' : 'family', sessionId: n.groupSessionId };
}

/**
 * The session page this note opens on, or null to open the note on its own (as before). Only
 * the kinds in `kinds` (default SESSION_PAGE_ON_OPEN, family today) are routed.
 */
export function sessionPageFor(
  n: SessionNoteRef,
  kinds: ReadonlySet<SessionPageKind> = SESSION_PAGE_ON_OPEN,
): { kind: SessionPageKind; sessionId: string } | null {
  const s = sessionOf(n);
  return s && kinds.has(s.kind) ? s : null;
}

/**
 * A link to one note in a chart's Clinical Notes (?note=<id> from the queue or a deep link, or an
 * Alerts Hub card's record): its session page, the note on its own, or nothing when the note is
 * not in the caller's list of this client's notes (as before: the chart just opens).
 */
export function noteLinkTarget<T extends SessionNoteRef & { id: string }>(
  notes: readonly T[],
  noteId: string,
):
  | { to: 'session'; kind: SessionPageKind; sessionId: string; note: T }
  | { to: 'note'; note: T }
  | null {
  const note = notes.find((n) => n.id === noteId);
  if (!note) return null;
  const page = sessionPageFor(note);
  return page ? { to: 'session', ...page, note } : { to: 'note', note };
}

/** One note in a client's Clinical Notes, as a link (calendar#46 step 9). */
export function chartNoteUrl(contactId: string, noteId: string): string {
  return `/?contact=${encodeURIComponent(contactId)}&view=clinical_notes&note=${encodeURIComponent(noteId)}`;
}

/**
 * A Progress Notes queue row (To review, Returned, My drafts, With reviewer, History):
 * - the author's own group draft opens in the group workspace (unchanged);
 * - everything else opens in the attendee's chart, Clinical Notes, on that note (?note=), where
 *   noteLinkTarget puts a family / couples note on its family page with that attendee selected.
 *   The page's Back is the single note's: back to the chart's Clinical Notes list. (The queue
 *   lists only notes on clients the user can open, ehr#622, so the chart opens for every row.)
 * A family note never goes to the group workspace, even the author's own draft.
 */
export function queueOpenTarget(
  row: SessionNoteRef & { appointmentKey?: string | null },
  mode: 'review' | 'edit' | 'view',
): 'group-workspace' | 'chart' {
  return mode === 'edit' && sessionOf(row)?.kind === 'group' && !!row.appointmentKey
    ? 'group-workspace'
    : 'chart';
}
